import * as am4charts from '@amcharts/amcharts4/charts';
import {
    ValueAxis, XYChart
} from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { RoundedRectangle } from '@amcharts/amcharts4/core';

import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { AbstractChartOptionsModel } from '../abstract-chart-options.model';
import { ChartModel } from '../chart.model';
import { DataPointModel } from '../data-point.model';

export interface ProgressChartOptionsModel extends AbstractChartOptionsModel {
    barHeight: number;
    seriesLength?: number;
    alternateLabelYPositions?: boolean;
    adjustLastLabelXPosition?: boolean;
    usesLegend?: boolean;
}

export class ProgressChartModel extends ChartModel {
    declare amchart: XYChart;
    declare options: ProgressChartOptionsModel;
    xAxis: ValueAxis;
    legend: am4charts.Legend;

    public initChart(element: HTMLElement): void {
        this.element = element;
        this.amchart = am4core.create(this.element, am4charts.XYChart);

        const yAxis = this.amchart.yAxes.push(new am4charts.CategoryAxis());
        yAxis.dataFields.category = 'category';
        yAxis.renderer.grid.template.disabled = true;
        yAxis.renderer.labels.template.disabled = true;

        this.xAxis = this.amchart.xAxes.push(new am4charts.ValueAxis());
        this.xAxis.renderer.grid.template.disabled = true;
        this.xAxis.renderer.labels.template.disabled = true;
        this.xAxis.min = 0;
        this.xAxis.max = 100;

        const series = this.amchart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueX = 'to';
        series.dataFields.openValueX = 'from';
        series.dataFields.categoryY = 'category';
        series.columns.template.propertyFields.fill = 'fill';
        series.columns.template.strokeOpacity = 0;
        series.columns.template.height = this.options.barHeight || 20;

        series.columns.template.column.adapter.add('cornerRadiusTopLeft', (radius, target) => this.getCornerRadius(target, true));
        series.columns.template.column.adapter.add('cornerRadiusTopRight', (radius, target) => this.getCornerRadius(target, false));
        series.columns.template.column.adapter.add('cornerRadiusBottomLeft', (radius, target) => this.getCornerRadius(target, true));
        series.columns.template.column.adapter.add('cornerRadiusBottomRight', (radius, target) => this.getCornerRadius(target, false));

        // Legend
        if (this.options.usesLegend) {
            this.legend = new am4charts.Legend();
            this.legend.parent = this.amchart.chartAndLegendContainer;
            this.legend.itemContainers.template.clickable = false;
            this.legend.itemContainers.template.focusable = false;
            this.legend.itemContainers.template.paddingTop = 3;
            this.legend.itemContainers.template.paddingBottom = 3;
            this.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
            this.legend.contentAlign = 'left';
            this.legend.markers.template.scale = 0.75;
            this.legend.marginTop = -25;
        }

        this.amchart.padding(
            this.options.styles?.customPadding.top,
            this.options.styles?.customPadding.right,
            this.options.styles?.customPadding.bottom,
            this.options.styles?.customPadding.left
        );
        if (this.options.alternateLabelYPositions) {
            this.amchart.paddingTop = 50;
        }

        this.amchart.chartContainer.height = 80;

        this.amchart.paddingLeft = 20;
    }

    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][]): void {
        if (!data) {
            return;
        }
        this.rawData = data;

        this.amchart.data = [];
        let hasValueMarker = false;
        data[0].forEach((dataPoint, index) => {
            if (!dataPoint.fill) {
                hasValueMarker = true;
            }
            this.amchart.data.push({
                category: '',
                from: dataPoint.from,
                to: dataPoint.to,
                name: dataPoint.name,
                labelDy: dataPoint.labelDy,
                fill: dataPoint.fill ? am4core.color(dataPoint.fill as string) : null,
                adjustXPosition: !(!this.options.adjustLastLabelXPosition && index === data[0].length - 1),
                adjustYPosition: this.options.alternateLabelYPositions && index % 2 === 1
            });
        });

        if (!this.options.usesLegend) {
            for (let i = 0; i < this.amchart.data.length; i++) {
                const dataPoint = this.amchart.data[i];
                if (dataPoint.from) {
                    // This is a bar with range
                    const range = this.xAxis.axisRanges.create();
                    range.value = dataPoint.to;
                    // move the label closer to the center of the bar, so that it is less likely to overflow on two sides
                    if (dataPoint.adjustXPosition) {
                        range.value = (dataPoint.from + dataPoint.to) / 2
                            - ((dataPoint.from + dataPoint.to) / 2 - 50) / 200 * (dataPoint.to - dataPoint.from);
                    }
                    range.label.text = !this.options.usesLegend ? dataPoint.label : '';
                    range.label.textAlign = 'middle';
                    range.label.fontSize = 12;
                    range.tick.strokeOpacity = 0.3;
                    const tickLength = dataPoint.tickLength ?? 10;
                    range.tick.length = tickLength;
                    if (dataPoint.adjustYPosition) {
                        range.tick.dy = 0 - this.options.barHeight - tickLength / 2;
                        range.label.dy = 0 - this.options.barHeight + dataPoint.labelDy;
                    }
                }
            }
        } else {
            this.legend.data = this.amchart.data.filter(dataPoint => dataPoint.from >= 0).map(dataPoint => {
                return { 'name': dataPoint.name, 'fill': dataPoint.fill };
            });
        }

        // It's for a value rather than a range
        if (hasValueMarker) {
            for (let i = 0; i < this.amchart.data.length; i++) {
                const dataPoint = this.amchart.data[i];
                if (dataPoint.fill) {
                    continue;
                }
                const point = this.xAxis.axisRanges.create();
                point.value = dataPoint.to;
                point.grid.stroke = am4core.color(chartColors.agprText);
                point.grid.strokeWidth = 1;
                point.grid.strokeOpacity = 1;
                point.grid.above = true;
                point.label.text = dataPoint.name;
                point.label.fill = am4core.color(chartColors.agprText);
                point.label.dy = -70;
                point.label.verticalCenter = 'top';
            }
        }

        this.updateSeriesStyles();
    }

    /**
     * Required but not currently used
     */
    updateSeriesStyles(): void {}

    /**
     * Exports data using amcharts
     */
    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {

        return this.amchart.exporting.export(type);
    }

    getCornerRadius(rect: RoundedRectangle, first: boolean): number {
        if (first) {
            return rect.dataItem.index === 0 ? 8 : 0;
        } else {
            return rect.dataItem.index === this.amchart.data.length - 1 ? 8 : 0;
        }
    }

    public isContentEmpty(): boolean {
        return false;
    }
}
